import { css } from '@emotion/react'
import { skipToken, useQuery } from '@tanstack/react-query'
import { memo } from 'react'

import { amountToString } from '@orus.eu/amount'
import type { AggregatedQuote } from '@orus.eu/dimensions'
import type { OperatingZone } from '@orus.eu/operating-zone'
import { getCurrencyPositionForOperatingZone } from '@orus.eu/operating-zone'
import {
  Badge,
  Button,
  Dialog,
  Header,
  NavbarLanguageSelector,
  spacing,
  Text,
  Tooltip,
  UserDropdown,
  useDialogVisibility,
  useLanguage,
  useOpenChat,
  useTranslate,
  useUiContext,
} from '@orus.eu/pharaoh'
import { languagesByOperatingZone } from '@orus.eu/product'
import type { OrganizationFunnelInfo } from '@orus.eu/right-access-management'

import { useTRPC } from '../../../../client.ts'
import { useDisconnect } from '../../../../lib/hooks/use-disconnect.ts'
import { useNavigateTo } from '../../../../lib/hooks/use-navigate-to-route.ts'
import { useSelectLanguage } from '../../../../lib/hooks/use-select-language.ts'
import { useSession } from '../../../../lib/session.ts'
import { usePermissions } from '../../../../lib/use-permissions.ts'
import { ContractChangeRecap } from '../../../organisms/contract-change-recap/contract-change-recap.tsx'
import type { CommonSubscriptionProps } from '../subscription-v2-props.ts'

import { ReferrerBadge } from './referrer-badge.tsx'

type SubscriptionV2HeaderProps = {
  compact?: boolean | undefined
  progress?: number | undefined
  title?: string | undefined
  locked?: boolean | undefined
  subscriptionId?: string | undefined
  endorsementId?: string | undefined
  quote?: AggregatedQuote | undefined
  referralCodeUsed?: string | undefined
  operatingZone?: OperatingZone | undefined
  organization?: OrganizationFunnelInfo | undefined
  context?: CommonSubscriptionProps['context'] | undefined
  customerId?: string | undefined
}

const lastOperatingZoneBySubscriptionId = new Map<string, OperatingZone>()

const HeaderQuoteAmountRecurrence = memo<{
  quote: AggregatedQuote
  operatingZone: OperatingZone | undefined
}>(function HeaderQuoteAmountRecurrence({ quote, operatingZone }) {
  const translate = useTranslate()
  const { paymentRecurrence } = quote
  const amount = paymentRecurrence === 'yearly' ? quote.yearlyTotalPremium : quote.subsequentMonthsTotalPremium
  const recurrenceLabelKey = `per_time_unit_${paymentRecurrence}` as 'per_time_unit_monthly' | 'per_time_unit_yearly'
  const currencyPosition = operatingZone ? getCurrencyPositionForOperatingZone(operatingZone) : undefined
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing[30]};
        margin-right: ${spacing[50]};
      `}
    >
      <Text variant="body2SemiBold">
        {amountToString(amount, {
          addCurrency: true,
          ...(currencyPosition ? { currencyPosition } : {}),
        })}
      </Text>
      <Badge variant="gray">{translate(recurrenceLabelKey)}</Badge>
    </div>
  )
})

export const SubscriptionV2Header = memo<SubscriptionV2HeaderProps>(function SubscriptionV2Header({
  compact,
  progress,
  title,
  locked,
  subscriptionId,
  endorsementId,
  quote,
  referralCodeUsed,
  operatingZone,
  organization,
  context,
  customerId,
}) {
  const { user } = useSession()
  const language = useLanguage()
  const handleLanguageSelected = useSelectLanguage()

  // operatingZone is briefly undefined while the next step's state loads. Since it never changes within
  // a subscription, we keep the last known value so the language selector doesn't unmount/remount (flash).
  if (operatingZone !== undefined && subscriptionId !== undefined) {
    lastOperatingZoneBySubscriptionId.set(subscriptionId, operatingZone)
  }
  const effectiveOperatingZone =
    operatingZone ?? (subscriptionId !== undefined ? lastOperatingZoneBySubscriptionId.get(subscriptionId) : undefined)

  const funnelLanguages = effectiveOperatingZone ? languagesByOperatingZone[effectiveOperatingZone] : undefined
  const languageSelector =
    funnelLanguages && funnelLanguages.length > 1 ? (
      <NavbarLanguageSelector
        config={{
          currentLanguage: language,
          onLanguageSelected: (selectedLanguage) => handleLanguageSelected(selectedLanguage, subscriptionId),
          availableLanguages: funnelLanguages,
        }}
        variant="funnel"
      />
    ) : undefined
  const {
    show: showRecapDialog,
    hide: hideRecapDialog,
    visible: isRecapDialogVisible,
  } = useDialogVisibility('endorsement-recap')
  const translate = useTranslate()
  const uiContext = useUiContext()

  const showRecapButton = user && subscriptionId && endorsementId
  let headerStatus: 'locked' | 'modification' | undefined
  if (locked) {
    headerStatus = 'locked'
  } else if (subscriptionId && endorsementId) {
    headerStatus = 'modification'
  } else {
    headerStatus = undefined
  }

  const hasQuoteAmountAndRecurrence =
    quote &&
    quote.paymentRecurrence &&
    (quote.paymentRecurrence === 'yearly'
      ? quote.yearlyTotalPremium != null
      : quote.subsequentMonthsTotalPremium != null)

  const rightBlock = showRecapButton ? (
    <>
      {uiContext === 'mobile' ? (
        <UserDropdownLoader />
      ) : (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing[30]};
          `}
        >
          {hasQuoteAmountAndRecurrence ? (
            <HeaderQuoteAmountRecurrence quote={quote} operatingZone={effectiveOperatingZone} />
          ) : null}
          <Tooltip title={translate('modifications_recap')}>
            <Button
              variant="secondary"
              size="medium"
              icon="clipboard-list-check-regular"
              onClick={showRecapDialog}
              aria-label={translate('modifications_recap')}
            />
          </Tooltip>
          <UserDropdownLoader />
        </div>
      )}
      {isRecapDialogVisible ? (
        <Dialog
          title={translate('modifications_recap')}
          illustration="invoice_big"
          size="large"
          onClose={hideRecapDialog}
          withSeparators={true}
          allowEmptyFooter={true}
        >
          <ContractChangeRecap
            amendmentId={endorsementId}
            subscriptionActionType="endorsement"
            status="pending"
            language={language}
            renewedPeriodIndex={undefined}
          />
        </Dialog>
      ) : null}
    </>
  ) : user ? (
    <UserDropdownLoader />
  ) : undefined

  return (
    <Header
      compact={compact}
      progress={progress}
      initialProgress={20}
      title={title}
      headerStatus={headerStatus}
      rightBlock={rightBlock}
      centerChip={
        <ReferrerBadge
          referralCodeUsed={referralCodeUsed}
          organization={organization}
          context={context}
          customerId={customerId}
        />
      }
      languageSelector={languageSelector}
    />
  )
})

const UserDropdownLoader = memo(function UserDropdownLoader() {
  const trpc = useTRPC()

  const openChat = useOpenChat()
  const disconnect = useDisconnect()
  const navigateToAccountPage = useNavigateTo({ to: '/' })

  const isCustomerApp = usePermissions().type === 'client'
  const customer = useQuery(trpc.users.loadMyInformation.queryOptions(isCustomerApp ? undefined : skipToken))

  if (!customer.data) return null

  return <UserDropdown onOpenChat={openChat} onOpenAccountPage={navigateToAccountPage} onLogout={disconnect} />
})
